import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Link,
} from "@mui/material";
import {
  OpenInNew,
  CheckCircle,
  Schedule,
  RateReview,
  LockOpen,
} from "@mui/icons-material";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { REOPEN_SUBMISSION } from "../../Queries";
import {
  SubmissionReviewActivity,
  SubmissionReviewSubmission,
  SubmissionReviewStatus,
  ReviewAllocationRecord,
} from "../../types";
import { SubmissionDetail } from "./SubmissionDetail";

interface Props {
  activity: SubmissionReviewActivity;
  submissions: SubmissionReviewSubmission[];
  onRefresh?: () => void;
}

export function ParticipantsList({ activity, submissions, onRefresh }: Props) {
  const [t] = useTranslation();
  const [selectedSubmission, setSelectedSubmission] =
    React.useState<SubmissionReviewSubmission | null>(null);

  // FL-284: instructor returns one student's submitted work to draft so
  // they can fix it (e.g. a bad upload) without reopening for everyone.
  const [reopenSubmission] = useMutation(REOPEN_SUBMISSION);
  const handleReopen = async (submissionId: string) => {
    await reopenSubmission({ variables: { submissionId } });
    onRefresh?.();
  };

  const showAllocations =
    activity.status !== SubmissionReviewStatus.Draft &&
    activity.status !== SubmissionReviewStatus.SubmissionsOpen;

  // Live presentations: rows are team presentations, not individual
  // submissions — there is no submission status to wait on.
  const isLive = activity.submissionType === "LIV";
  // Holistic team submissions label rows by team regardless of format.
  const isTeamRows = isLive || activity.submitterMode === "TM";

  return (
    <Card variant="outlined">
      <CardHeader
        title={`${String(t(isLive ? "Presentations" : "Participants"))} (${
          submissions.length
        })`}
        titleTypographyProps={{ variant: "h6" }}
      />
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t(isTeamRows ? "Team" : "Student")}</TableCell>
              {!isLive && <TableCell>{t("Submission")}</TableCell>}
              {activity.status === SubmissionReviewStatus.SubmissionsOpen && (
                <TableCell>{t("Link")}</TableCell>
              )}
              {showAllocations && <TableCell>{t("Reviewed By")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.map((submission) => {
              // Allocations for this submission: the students writing the
              // reviews of this row's team/work.
              const reviewedByAllocations = submission.allocations || [];

              return (
                <TableRow key={submission.id}>
                  {/* Student name (clickable to open detail); team name for
                      live presentations */}
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setSelectedSubmission(submission)}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                    >
                      {isTeamRows
                        ? submission.groupName ?? submission.authorName
                        : submission.authorName}
                    </Link>
                  </TableCell>

                  {/* Submission status (not applicable to live presentations) */}
                  {!isLive && (
                    <TableCell>
                      <Chip
                        icon={
                          submission.status === "SB" ? (
                            <CheckCircle fontSize="small" />
                          ) : (
                            <Schedule fontSize="small" />
                          )
                        }
                        label={
                          submission.status === "SB"
                            ? String(t("Submitted"))
                            : String(t("Pending"))
                        }
                        color={
                          submission.status === "SB" ? "success" : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  )}

                  {/* Submission link (only when submissions open) */}
                  {activity.status ===
                    SubmissionReviewStatus.SubmissionsOpen && (
                    <TableCell>
                      {submission.tokenizedLink && (
                        <Tooltip
                          title={String(
                            t("Open submission page as this student"),
                          )}
                        >
                          <IconButton
                            component="a"
                            href={submission.tokenizedLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {submission.status === "SB" && (
                        <Tooltip
                          title={String(
                            t(
                              "Reopen this submission so the student can edit and resubmit",
                            ),
                          )}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleReopen(submission.id)}
                            aria-label={String(t("Reopen submission"))}
                          >
                            <LockOpen fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}

                  {/* Who reviews this row's team/work: one chip per
                      reviewer with completion status, plus a link to open
                      that reviewer's review. */}
                  {showAllocations && (
                    <TableCell>
                      {reviewedByAllocations.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {reviewedByAllocations.map((alloc) => (
                            <Box
                              key={alloc.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                icon={
                                  alloc.reviewSubmitted ? (
                                    <CheckCircle fontSize="small" />
                                  ) : (
                                    <RateReview fontSize="small" />
                                  )
                                }
                                label={alloc.reviewerName}
                                size="small"
                                color={
                                  alloc.reviewSubmitted ? "success" : "warning"
                                }
                                variant="outlined"
                              />
                              {alloc.reviewLink && (
                                <Tooltip
                                  title={String(
                                    t("Open review as this student"),
                                  )}
                                >
                                  <IconButton
                                    component="a"
                                    href={alloc.reviewLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    sx={{ p: 0.25 }}
                                  >
                                    <OpenInNew sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      {/* Submission detail modal */}
      {selectedSubmission && (
        <SubmissionDetail
          open={true}
          onClose={() => setSelectedSubmission(null)}
          submission={selectedSubmission}
          activity={activity}
          onRefresh={onRefresh ?? (() => {})}
        />
      )}
    </Card>
  );
}
